import { useEffect, useState } from "react";
import { Loader2, Settings2, Sunrise, Sunset, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const WINDOWS = [
  { value: "morning", label: "6:00 AM – 9:00 AM", icon: Sunrise },
  { value: "evening", label: "6:00 PM – 9:00 PM", icon: Sunset },
];

export default function ManagePlan({ className = "" }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    if (open && user?.email && !sub) {
      setEmail(user.email);
      (async () => {
        try {
          const { data } = await api.post("/subscribers/lookup", { email: user.email });
          setSub(data);
        } catch {}
      })();
    }
  }, [open, user, sub]);

  const lookup = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/subscribers/lookup", { email });
      setSub(data);
    } catch {
      toast.error("No active subscription found for that email.");
    }
    setLoading(false);
  };

  const switchWindow = async (value) => {
    if (sub.pickup_window === WINDOWS.find((w) => w.value === value).label) return;
    setSwitching(true);
    try {
      const { data } = await api.post("/subscribers/switch-window", { email, pickup_window: value });
      setSub({ ...sub, pickup_window: data.pickup_window });
      toast.success(`Window updated to ${data.pickup_window}. Confirmation email sent.`);
    } catch {
      toast.error("Could not switch window. Try again.");
    }
    setSwitching(false);
  };

  const openPortal = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/portal", { email, origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch {
      toast.error("No subscription billing account found for that email.");
      setLoading(false);
    }
  };

  const reset = (v) => {
    setOpen(v);
    if (!v) { setSub(null); setEmail(""); }
  };

  return (
    <>
      <button data-testid="manage-plan-button" onClick={() => setOpen(true)} className={className}>
        Manage Plan
      </button>
      <Dialog open={open} onOpenChange={reset}>
        <DialogContent className="bg-ink border-white/10 text-parchment sm:max-w-sm" data-testid="manage-plan-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-terracotta" /> Manage your plan
            </DialogTitle>
            <DialogDescription className="text-mist text-sm">
              {sub
                ? `Unit ${sub.unit_number} · ${sub.plan_name}`
                : "Enter the email you subscribed with to switch pickup windows, update your card, or cancel."}
            </DialogDescription>
          </DialogHeader>

          {!sub ? (
            <form onSubmit={lookup} className="space-y-4 mt-2">
              <div>
                <Label htmlFor="mp-email" className="text-xs text-mist">Subscription email</Label>
                <Input id="mp-email" type="email" data-testid="manage-plan-email-input" value={email}
                  onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com"
                  className="mt-1.5 bg-white/5 border-white/10 text-parchment placeholder:text-dim" required />
              </div>
              <button type="submit" data-testid="manage-plan-lookup-submit" disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-terracotta hover:bg-parchment hover:text-charcoal text-parchment font-semibold text-sm py-3.5 rounded-full transition-colors duration-300 disabled:opacity-60">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {loading ? "Looking up…" : "Find My Plan"}
              </button>
            </form>
          ) : (
            <div className="space-y-5 mt-2" data-testid="manage-plan-details">
              <div>
                <p className="text-xs text-mist mb-2">Pickup window — tap to switch</p>
                <div className="grid grid-cols-2 gap-2">
                  {WINDOWS.map((w) => {
                    const active = sub.pickup_window === w.label;
                    return (
                      <button key={w.value} type="button" disabled={switching}
                        data-testid={`window-switch-${w.value}`}
                        onClick={() => switchWindow(w.value)}
                        className={`flex flex-col items-center gap-1.5 rounded-2xl border px-3 py-4 text-xs font-medium transition-colors duration-300 disabled:opacity-60 ${
                          active
                            ? "border-terracotta bg-terracotta/15 text-parchment"
                            : "border-white/10 bg-white/[0.03] text-mist hover:border-terracotta/50 hover:text-parchment"
                        }`}>
                        {switching ? <Loader2 className="w-4 h-4 animate-spin" /> : <w.icon className={`w-4 h-4 ${active ? "text-terracotta" : ""}`} />}
                        {w.label}
                        {active && <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-terracotta">Current</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
              {sub.is_subscription && (
                <button onClick={openPortal} data-testid="manage-plan-portal-button" disabled={loading}
                  className="w-full flex items-center justify-center gap-2 border border-white/15 hover:border-terracotta text-parchment font-semibold text-sm py-3.5 rounded-full transition-colors duration-300 disabled:opacity-60">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  Billing, Invoices & Cancel <ArrowRight className="w-4 h-4" />
                </button>
              )}
              <p className="text-center font-mono text-[10px] uppercase tracking-[0.2em] text-dim">
                Window changes apply from the next service night
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
